var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import styles from './video-list-page.styles';
import '../video-list/video-list';
import '../breadcrumbs/breadcrumbs';
import '../breadcrumbs/breadcrumb-item/breadcrumb-item';
import '../skeleton/skeleton';
import { getVideoSkeletonLayout } from '../skeleton/video-skeleton';
import { ifDefined } from 'lit/directives/if-defined.js';
import { dictionary, getPathId } from '@mf-cms/helpers';
import { classMap } from 'lit/directives/class-map.js';
import { MFElement } from '../mfelement/mfelement';
let PVideoListPage = class PVideoListPage extends MFElement {
    constructor() {
        super(...arguments);
        this.videos = [];
        this.liveVideos = [];
        this.openFoldersInTree = [];
        this.titleSignature = '';
        this.items = 0;
        this.heading = '';
        this.sort = 'id';
        this.sortOrder = 'asc';
        this.videoBaseUrl = '';
        this.LiveBaseUrl = '';
        this.baseUrl = '';
        this.showLatest = false;
        this.culture = 'en-GB';
        this.showRuntime = false;
        this.pageDesign = 'default';
        this.hideDescription = false;
        this.itemLayout = 'default';
        this.loading = true;
        this.initialSort = 'name';
        this.initialSortOrder = 'asc';
        this.videosRequest = 0;
        /**
         * Reload folder content when browser Back/Forward changes the folder in the URL.
         * @returns {void} side effects only
         */
        this.handlePopState = () => {
            const folderId = getPathId(window.location.pathname) ?? this.folderTree?.[0]?.id;
            if (!folderId || folderId == this.folderId)
                return;
            this.showFolder(folderId);
        };
        this.subFolders = (parent, folderTree) => {
            return html ` <p-folder-tree slot="sub" sub> ${folderTree?.filter((f) => f.parent == parent)?.map((folder) => html `
              <p-folder
                @p-folder-select=${(e) => this.pickFolder(e)}
                @p-folder-toggle-open=${() => {
                folder.open = !folder.open;
                this.requestUpdate();
            }}
                ?selected=${folder.id == this.folderId}
                ?open=${folder.hasChildren && (this.isFolderOpen(folder) || folder.open)}
                hide-icon
                folder-id=${folder.id}
                level=${folder.depth}
                ?has-sub-folders=${folder.hasChildren}
                folder-title=${folder.name}
                culture=${this.culture}
              >
                ${folder.name}
                ${folder.hasChildren && (this.isFolderOpen(folder) || folder.open)
                ? this.subFolders(folder.id, folderTree)
                : ''}
              </p-folder>
            `)}
      </p-folder-tree>
    `;
        };
    }
    static { this.styles = styles; }
    async getVideos(folderId) {
        this.videos = [];
        if (folderId == 'latest' || isNaN(folderId)) {
            try {
                const response = await fetch(`${process.env.API_URL}/files/latest?fields=any&type=video&access_token=${this.accessToken}&sort=${this.sort}&order=${this.sortOrder}`);
                const json = await response.json();
                //TODO: log?
                return json;
            }
            catch (e) {
                console.error(e);
            }
            return [];
        }
        try {
            const response = await fetch(`${process.env.API_URL}/folder/${folderId}/files?fields=any&type=video&access_token=${this.accessToken}&sort=${this.sort}&order=${this.sortOrder}`);
            const json = await response.json();
            window.dispatchEvent(new CustomEvent('action', {
                detail: {
                    type: 'listFilesInFolder',
                    id: folderId,
                    culture: this.culture
                }
            }));
            return json;
        }
        catch (e) {
            console.error(e);
        }
        return [];
    }
    async getLiveVideos(folderId) {
        if (folderId == 'latest' || !folderId)
            return [];
        let response;
        const url = `${process.env.API_URL}/folder/${folderId}/livestreams?fields=any&access_token=${this.accessToken}&sort=liveAt&order=desc`;
        try {
            response = await fetch(url);
        }
        catch (e) {
            console.error(e);
            return [];
        }
        if (response.status !== 200) {
            console.error(`Could not fetch livestream, error: ${response.status}`);
            return [];
        }
        const json = await response.json();
        return json.sort((a, b) => {
            if (a.status === 'BROADCASTING' && b.status !== 'BROADCASTING')
                return -1;
            if (a.status !== 'BROADCASTING' && b.status === 'BROADCASTING')
                return 1;
            return new Date(a.liveAt).getTime() - new Date(b.liveAt).getTime();
        });
    }
    async getFolderTree() {
        const response = await fetch(`${process.env.API_URL}/foldertree?fields=any&access_token=${this.accessToken}`);
        const json = await response.json();
        return json;
    }
    async getFolder(folderId) {
        if (folderId == 'latest') {
            return {
                id: 'latest',
                name: await dictionary(this.culture, 'videosPage', 'latest'),
                parents: []
            };
        }
        const response = await fetch(`${process.env.API_URL}/folder/${folderId}?fields=any&access_token=${this.accessToken}`);
        const json = await response.json();
        return json[0];
    }
    /**
     * Load videos + live videos for a folder, toggling the loading flag for the skeleton state.
     * @param {number | string} folderId folder to load
     * @returns {Promise<void>}
     */
    async loadVideos(folderId) {
        const request = ++this.videosRequest;
        this.loading = true;
        try {
            const videos = await this.getVideos(folderId);
            const liveVideos = await this.getLiveVideos(folderId);
            if (request !== this.videosRequest)
                return;
            this.videos = videos;
            this.liveVideos = liveVideos;
        }
        finally {
            if (request === this.videosRequest)
                this.loading = false;
        }
    }
    //on created
    async firstUpdated() {
        this.initialSort = this.sort;
        this.initialSortOrder = this.sortOrder;
        this.titleSignature = document.title;
        this.accessToken = window.getAccessToken();
        this.folderTree = await this.getFolderTree();
        if (this.showLatest) {
            this.folderTree.unshift({
                id: 'latest',
                name: await dictionary(this.culture, 'menu', 'latest'),
                hasChildren: false,
                depth: 0
            });
        }
        if (!this.folderId && this.folderTree.length > 0) {
            this.folderId = this.folderTree[0].id;
            // window.history.pushState({}, '', `${this.baseUrl}/${this.folderId}`);
        }
        this.pickedFolder = await this.getFolder(this.folderId);
        await this.loadVideos(this.folderId);
        document.title = `${this.pickedFolder.name} ${this.titleSignature}`;
        // start live ticker for live videos, update every 10 seconds
        this.interval = setInterval(async () => {
            this.liveVideos = await this.getLiveVideos(this.folderId);
        }, 10000);
    }
    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('popstate', this.handlePopState);
    }
    disconnectedCallback() {
        clearInterval(this.interval);
        window.removeEventListener('popstate', this.handlePopState);
        super.disconnectedCallback();
    }
    async updated() {
        if ((this.sort && this.initialSort !== this.sort) ||
            (this.sortOrder && this.initialSortOrder !== this.sortOrder)) {
            this.initialSort = this.sort;
            this.initialSortOrder = this.sortOrder;
            await this.loadVideos(this.folderId);
        }
        this.openFoldersInTree = [...(this.folderTreeElement?.querySelectorAll('p-folder') || [])]
            .filter((f) => f.open && f.children.length > 0)
            .map((x) => x.folderId.toString());
    }
    async pickFolder(e) {
        // A sub folder sits inside its parent p-folder, whose listener would pick it a second time
        e.stopPropagation();
        const selectedFolderComponent = e.target;
        const selectedId = selectedFolderComponent.folderId;
        window.history.pushState({}, '', `${this.baseUrl}/${selectedId}`);
        await this.showFolder(selectedId);
    }
    /**
     * Load a folder's content and move focus to its heading.
     * @param {string} selectedId folder id
     * @returns {Promise<void>} resolves once the folder is shown and focused
     */
    async showFolder(selectedId) {
        this.folderId = selectedId;
        const folder = await this.getFolder(selectedId);
        if (this.folderId != selectedId)
            return;
        this.pickedFolder = folder;
        document.title = `${this.pickedFolder.name} ${this.titleSignature}`;
        await this.loadVideos(selectedId);
        if (this.folderId != selectedId)
            return;
        await this.updateComplete;
        this.focusVideoHeader();
        const metaDescrEl = document.querySelector('meta[name="description"]');
        const metaOGDescrEl = document.querySelector('meta[property="og:description"]');
        metaDescrEl?.setAttribute('content', this.pickedFolder.name);
        metaOGDescrEl?.setAttribute('content', this.pickedFolder.name);
        this.updateHeader(selectedId);
    }
    /**
     * Move focus to the folder heading so screen readers announce it.
     * @returns {void} side effects only
     */
    focusVideoHeader() {
        this.videoHeaderElement?.focus();
    }
    /**
     * Update header
     * @param {string} selectedId selected folder id
     * @returns {void}
     */
    updateHeader(selectedId) {
        const headerEl = document.querySelector('p-header');
        const selectedPageInNavigationEl = headerEl?.shadowRoot?.querySelector('nav.nav li.selected');
        const folderlistElements = selectedPageInNavigationEl?.getElementsByTagName('li');
        if (!folderlistElements) {
            return;
        }
        for (let i = 0; i < folderlistElements.length; i++) {
            const listElement = folderlistElements[i];
            const linkElement = listElement.querySelector('a');
            const link = linkElement?.href || '';
            if (link.includes(selectedId)) {
                listElement?.classList.add('selected');
                continue;
            }
            listElement?.classList.remove('selected');
        }
    }
    isFolderOpen(folder) {
        return (this.pickedFolder &&
            (this.pickedFolder.id == folder.id ||
                (this.pickedFolder.parents != null &&
                    this.pickedFolder.parents.filter((x) => x.id == folder.id).length > 0) ||
                this.openFoldersInTree.filter((x) => x == folder.id).length > 0));
    }
    /**
     * Render component
     * @returns {TemplateResult} Template
     */
    render() {
        const liveVideos = this.liveVideos.map((video) => ({
            id: video.id,
            name: video.title,
            description: video.description,
            poster: video.poster,
            length: null,
            uploaded: video.liveAt
        }));
        const listItems = [...liveVideos, ...this.videos];
        const classes = {
            [`page-layout-${this.pageDesign}`]: true,
            [`item-layout-${this.itemLayout}`]: true
        };
        return html ` <section class="video-list-page ${classMap(classes)}"> <div class="container"> ${this.pageDesign == 'hideFolderTree' ? '' : html ` <div class="file-tree">
                <a
                  class="skip-link"
                  href="#"
                  @click=${(e) => {
            e.preventDefault();
            this.focusVideoHeader();
        }}
                  >${dictionary(this.culture, 'videoListPage', 'skipToVideos')}</a
                >
                <p-folder-tree
                  slot="sidebar"
                  tree-id="folder-tree"
                  heading=${dictionary(this.culture, 'videoListPage', 'categories')}
                >
                  ${this.folderTree
            ?.filter((f) => f.depth == 0)
            ?.map((folder) => html ` <p-folder @p-folder-select=${(e) => this.pickFolder(e)} @p-folder-toggle-open=${() => { folder.open = !folder.open; this.requestUpdate(); }} hide-icon ?selected=${folder.id == this.folderId} ?open=${folder.hasChildren && (this.isFolderOpen(folder) || folder.open)} folder-id=${folder.id} level=${folder.depth} ?has-sub-folders=${folder.hasChildren} folder-title=${folder.name} culture=${this.culture} > ${folder.name} ${folder.hasChildren && (this.isFolderOpen(folder) || folder.open) ? this.subFolders(folder.id, this.folderTree) : ''} </p-folder> `)}
                </p-folder-tree>
              </div>`}
          <div class="main">
            <p-breadcrumbs culture=${this.culture}>
              ${this.pickedFolder?.parents
            ? [...this.pickedFolder.parents].reverse().map((parent) => html ` <p-breadcrumb-item link="${this.baseUrl}/${parent.id}" >${parent.name}</p-breadcrumb-item > `)
            : ''}
              <p-breadcrumb-item currentPage link="${this.baseUrl}/${this.pickedFolder?.id}"
                >${this.pickedFolder?.name}</p-breadcrumb-item
              >
            </p-breadcrumbs>
            <header
              class="video-header"
              tabindex="-1"
              aria-label=${dictionary(this.culture, 'videoListPage', 'ariaVideoCategory')}
              aria-busy=${this.loading ? 'true' : 'false'}
            >
              ${this.pickedFolder?.name ? html ` <h1>${this.pickedFolder.name}</h1> ` : ''}
              ${this.loading
            ? html `<p-skeleton class="items" width="80px" height="15px" margin="0"></p-skeleton>`
            : html `<span class="items" role="text" >${listItems.length} ${listItems.length == 1 ? dictionary(this.culture, 'videoListPage', 'video') : dictionary(this.culture, 'videoListPage', 'videos')}</span >`}
            </header>
            <ul class="video-listing">
              ${this.loading
            ? [...Array(6)].map(() => html ` <li>${getVideoSkeletonLayout(this.itemLayout)}</li> `)
            : ''}
              ${!this.loading && this.liveVideos && this.liveVideos.length > 0
            ? this.liveVideos.map((video) => html ` <li> <p-video-item layout=${this.itemLayout} culture=${this.culture} heading-tag="h2" ?is-live=${video.status == 'BROADCASTING'} ?is-upcoming=${video.status == 'PENDING'} ?ended=${video.status == 'FINISHED'} link=${this.LiveBaseUrl + '/' + video.id} video-id=${video.id} summary=${video.description} heading=${video.title} poster=${video.poster} date=${video.liveAt} ?hide-description=${this.hideDescription} > </p-video-item> </li> `)
            : ''}
              ${!this.loading && this.videos && this.videos.length > 0
            ? this.videos.map((video) => html ` <li> <p-video-item layout=${this.itemLayout} culture=${this.culture} heading-tag="h2" link=${this.videoBaseUrl + '/' + video.id} video-id=${video.id} summary=${video.description} heading=${video.name} poster=${video.poster} duration=${this.showRuntime ? ifDefined(video.length) : ''} ?hide-description=${this.hideDescription} > </p-video-item> </li> `)
            : ''}
            </ul>
          </div>
        </div>
      </section>
    `;
    }
};
__decorate([
    property()
], PVideoListPage.prototype, "accessToken", void 0);
__decorate([
    property()
], PVideoListPage.prototype, "folderTree", void 0);
__decorate([
    property()
], PVideoListPage.prototype, "videos", void 0);
__decorate([
    property()
], PVideoListPage.prototype, "liveVideos", void 0);
__decorate([
    property()
], PVideoListPage.prototype, "pickedFolder", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'folder-id' })
], PVideoListPage.prototype, "folderId", void 0);
__decorate([
    property({ type: Number, reflect: true })
], PVideoListPage.prototype, "items", void 0);
__decorate([
    property({ type: String, reflect: true })
], PVideoListPage.prototype, "heading", void 0);
__decorate([
    property({ type: String, reflect: true })
], PVideoListPage.prototype, "sort", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'sort-order' })
], PVideoListPage.prototype, "sortOrder", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'video-base-url' })
], PVideoListPage.prototype, "videoBaseUrl", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'live-base-url' })
], PVideoListPage.prototype, "LiveBaseUrl", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'base-url' })
], PVideoListPage.prototype, "baseUrl", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-latest' })
], PVideoListPage.prototype, "showLatest", void 0);
__decorate([
    property({ type: String })
], PVideoListPage.prototype, "culture", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-runtime' })
], PVideoListPage.prototype, "showRuntime", void 0);
__decorate([
    property({ type: String, attribute: 'page-design' })
], PVideoListPage.prototype, "pageDesign", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'hide-description' })
], PVideoListPage.prototype, "hideDescription", void 0);
__decorate([
    property({ type: String, attribute: 'item-layout' })
], PVideoListPage.prototype, "itemLayout", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PVideoListPage.prototype, "loading", void 0);
__decorate([
    query('p-folder-tree')
], PVideoListPage.prototype, "folderTreeElement", void 0);
__decorate([
    query('.video-header')
], PVideoListPage.prototype, "videoHeaderElement", void 0);
PVideoListPage = __decorate([
    customElement('p-video-list-page')
    /*
      TODO: fix error message if no folders is avaible
    */
    /**
     * PVideoListPage component
     *
     * @class PVideoListPage
     * @typedef {PVideoListPage}
     * @extends {LitElement}
     */
], PVideoListPage);
export { PVideoListPage };
